/**
 * Faana-specific scoring and color utilities
 * Based on Faana's Growth Operating Framework and assessment methodology
 */

import { BehavioralZone, GrowthIndicator, HealthCategory } from '../types';

/**
 * Health Score Color Mapping (0-100)
 * Based on Faana's health categorization
 */
export const getFaanaHealthColor = (score: number): string => {
  if (score >= 80) return '#4CAF50'; // Green - Thriving
  if (score >= 60) return '#FBC02D'; // Yellow - Healthy
  if (score >= 40) return '#FF9800'; // Orange - Concerning
  return '#D32F2F'; // Red - Critical
};

/**
 * Get health category from score
 */
export const getHealthCategory = (score: number): HealthCategory => {
  if (score >= 80) return 'thriving';
  if (score >= 60) return 'healthy';
  if (score >= 50) return 'stable';
  if (score >= 40) return 'strained';
  return 'critical';
};

/**
 * Behavioral Zone Color Mapping
 * Based on Faana's Leadership Risk & Growth Index
 */
export const getFaanaZoneColor = (zone: BehavioralZone): string => {
  switch (zone) {
    case 'learning': return '#2196F3'; // Blue - Optimal zone
    case 'comfort': return '#FFC107'; // Amber - Risk of stagnation
    case 'panic': return '#F44336'; // Red - Dysfunction
    default: return '#9E9E9E'; // Grey - Unknown
  }
};

/**
 * Get zone description
 */
export const getZoneDescription = (zone: BehavioralZone): string => {
  switch (zone) {
    case 'learning': return 'Optimal challenge/support balance';
    case 'comfort': return 'Low risk, low growth - intervention opportunity';
    case 'panic': return 'Overwhelm, breakdown risk - urgent attention';
    default: return 'Zone not assessed';
  }
};

/**
 * Dimension Score Color (0-10 scale)
 * Based on Faana's 10-point assessment scale
 */
export const getDimensionColor = (score: number): string => {
  if (score >= 9) return '#1B5E20'; // Dark Green - Peak Performance
  if (score >= 7) return '#4CAF50'; // Green - Growth Zone
  if (score >= 5) return '#FBC02D'; // Yellow - Learning Zone
  if (score >= 3) return '#FF9800'; // Orange - Comfort Zone
  return '#D32F2F'; // Red - Panic Zone
};

/**
 * Get dimension zone label
 */
export const getDimensionZone = (score: number): string => {
  if (score >= 9) return 'Peak Performance';
  if (score >= 7) return 'Growth Zone';
  if (score >= 5) return 'Learning Zone';
  if (score >= 3) return 'Comfort Zone';
  return 'Panic Zone';
};

/**
 * Growth Indicator Icons
 */
export const getGrowthIndicator = (indicator: GrowthIndicator): { icon: string; color: string; label: string } => {
  switch (indicator) {
    case 'accelerating':
      return { icon: '📈', color: '#4CAF50', label: 'Accelerating growth' };
    case 'steady':
      return { icon: '➡️', color: '#2196F3', label: 'Steady progress' };
    case 'plateaued':
      return { icon: '🔄', color: '#FFC107', label: 'Plateaued - intervention opportunity' };
    case 'declining':
      return { icon: '📉', color: '#FF5722', label: 'Declining - urgent attention' };
    case 'volatile':
      return { icon: '⚡', color: '#9C27B0', label: 'Volatile - deeper analysis needed' };
  }
};

/**
 * Trust Indicator Scoring
 * Based on Faana Trust Framework: Consistency, Competence, Care, Communication
 */
export const getTrustIndicators = (trustScore: number): {
  consistency: number;
  competence: number;
  care: number;
  communication: number;
} => {
  // Simplified distribution - in real implementation would be based on evidence
  const base = trustScore / 10;
  return {
    consistency: base + (Math.random() - 0.5),
    competence: base + (Math.random() - 0.5),
    care: base + (Math.random() - 0.5),
    communication: base + (Math.random() - 0.5),
  };
};

/**
 * Confidence Level Opacity for visualizations
 */
export const getConfidenceOpacity = (confidence: string): number => {
  switch (confidence) {
    case 'HIGH': return 1.0;
    case 'MEDIUM': return 0.7;
    case 'LOW': return 0.4;
    default: return 0.5;
  }
};

/**
 * Get intervention urgency based on health score and trajectory
 */
export const getInterventionUrgency = (
  healthScore: number,
  trajectory?: string
): { level: 'critical' | 'high' | 'medium' | 'low'; color: string; message: string } => {
  if (healthScore < 40 || trajectory === 'declining') {
    return {
      level: 'critical',
      color: '#D32F2F',
      message: 'Immediate intervention required'
    };
  }
  if (healthScore < 60) {
    return {
      level: 'high',
      color: '#FF5722',
      message: 'Priority intervention recommended'
    };
  }
  if (healthScore < 80 || trajectory === 'plateaued') {
    return {
      level: 'medium',
      color: '#FF9800',
      message: 'Proactive intervention opportunity'
    };
  }
  return {
    level: 'low',
    color: '#4CAF50',
    message: 'Monitor and maintain'
  };
};

/**
 * Format Faana assessment dimension for display
 */
export const formatDimensionName = (dimension: string): string => {
  const formatted: Record<string, string> = {
    trust: 'Trust',
    communication: 'Communication',
    psychologicalSafety: 'Psychological Safety',
    mutualSupport: 'Mutual Support',
    conflictManagement: 'Conflict Management',
    growthOrientation: 'Growth Orientation',
  };
  return formatted[dimension] || dimension;
};

/**
 * Get recommendation priority color
 */
export const getRecommendationColor = (priority: 'high' | 'medium' | 'low'): string => {
  switch (priority) {
    case 'high': return '#D32F2F';
    case 'medium': return '#FF9800';
    case 'low': return '#4CAF50';
    default: return '#9E9E9E';
  }
};

/**
 * Calculate network health score from relationship health scores
 */
export const calculateNetworkHealth = (healthScores: number[]): {
  score: number;
  category: string;
  trend: 'improving' | 'stable' | 'declining';
} => {
  if (healthScores.length === 0) {
    return { score: 0, category: 'No data', trend: 'stable' };
  }

  const average = healthScores.reduce((a, b) => a + b, 0) / healthScores.length;
  const category = getHealthCategory(average);

  // Simple trend calculation - in real implementation would use temporal data
  const recentAverage = healthScores.slice(-5).reduce((a, b) => a + b, 0) / Math.min(5, healthScores.length);
  const trend = recentAverage > average + 5 ? 'improving' :
                recentAverage < average - 5 ? 'declining' : 'stable';

  return { score: average, category, trend };
};

/**
 * Generate health gradient for visualizations
 */
export const generateHealthGradient = (id: string): string => {
  return `
    <defs>
      <linearGradient id="${id}" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" style="stop-color:${getFaanaHealthColor(0)};stop-opacity:1" />
        <stop offset="40%" style="stop-color:${getFaanaHealthColor(40)};stop-opacity:1" />
        <stop offset="60%" style="stop-color:${getFaanaHealthColor(60)};stop-opacity:1" />
        <stop offset="80%" style="stop-color:${getFaanaHealthColor(80)};stop-opacity:1" />
        <stop offset="100%" style="stop-color:${getFaanaHealthColor(100)};stop-opacity:1" />
      </linearGradient>
    </defs>
  `;
};

/**
 * Get actionable insight based on dimension scores
 */
export const getActionableInsight = (
  dimension: string,
  score: number
): { insight: string; action: string } => {
  const insights: Record<string, { low: { insight: string; action: string }; high: { insight: string; action: string } }> = {
    trust: {
      low: {
        insight: 'Trust deficit detected - consistency and reliability concerns',
        action: 'Implement weekly check-ins and follow-through tracking'
      },
      high: {
        insight: 'Strong trust foundation established',
        action: 'Leverage trust for increased autonomy and innovation'
      }
    },
    communication: {
      low: {
        insight: 'Communication gaps creating misalignment',
        action: 'Establish structured communication protocols and feedback loops'
      },
      high: {
        insight: 'Effective communication patterns in place',
        action: 'Document and share communication best practices'
      }
    },
    psychologicalSafety: {
      low: {
        insight: 'Team members hesitant to share ideas or concerns',
        action: 'Facilitate psychological safety workshop and model vulnerability'
      },
      high: {
        insight: 'High psychological safety enabling innovation',
        action: 'Encourage calculated risk-taking and experimentation'
      }
    },
    mutualSupport: {
      low: {
        insight: 'Limited peer support and knowledge sharing',
        action: 'Create peer mentoring program and collaboration incentives'
      },
      high: {
        insight: 'Strong mutual support network active',
        action: 'Recognize and celebrate support behaviors'
      }
    },
    conflictManagement: {
      low: {
        insight: 'Conflicts avoided or handled destructively',
        action: 'Provide conflict resolution training and mediation support'
      },
      high: {
        insight: 'Conflicts resolved constructively',
        action: 'Share conflict resolution success stories'
      }
    },
    growthOrientation: {
      low: {
        insight: 'Limited focus on learning and development',
        action: 'Implement individual development plans and learning goals'
      },
      high: {
        insight: 'Strong growth mindset and continuous learning',
        action: 'Create stretch assignments and mentoring opportunities'
      }
    }
  };

  const dimensionInsights = insights[dimension];
  if (!dimensionInsights) {
    return { insight: 'Assessment needed', action: 'Conduct detailed assessment' };
  }

  return score < 5 ? dimensionInsights.low : dimensionInsights.high;
};